<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue';
import { isEqual } from 'lodash';

import { MapTypeId } from '@libs/maps';
import { AppLinkButton } from '@libs/ui';

import { MapType, DynamicFullScreenMap } from '../maps';
import {
  useBrowserStore,
  useMapStore,
  useStatsigEventsStore,
} from '../../core/stores';
import type {
  ActivityDynamicData,
  ActivityStaticData,
} from '../activityDetails';

import { GalleryType } from './models';
import ActivityDetailsModals from './components/ActivityDetailsModals.vue';
import ActivityDetailsPanels from './components/ActivityDetailsPanels.vue';
import {
  useActivityDetailsModalsStore,
  useActivityDetailsPanelsStore,
  useActivityDetailsStore,
  useActivityDetailsMapStore,
} from './stores';
import ActivityDetailsLayout from './components/ActivityDetailsLayout.vue';
import ActivityDetailsHeader from './components/ActivityDetailsHeader.vue';
import ActivityDetailsMainDescription from './components/ActivityDetailsMainDescription.vue';
import ActivityDetailsMainHeader from './components/ActivityDetailsMainHeader.vue';
import ActivityDetailsMainHeaderLoader from './components/loaders/ActivityDetailsMainHeaderLoader.vue';
import ActivityDetailsGallery from './components/ActivityDetailsGallery.vue';
import ActivityDetailsGalleryLoader from './components/loaders/ActivityDetailsGalleryLoader.vue';
import ActivityDetailsPriceSummaryDesktop from './components/ActivityDetailsPriceSummaryDesktop.vue';
import ActivityDetailsPriceSummaryDesktopLoader from './components/loaders/ActivityDetailsPriceSummaryDesktopLoader.vue';
import ActivityDetailsPriceSummaryMobile from './components/ActivityDetailsPriceSummaryMobile.vue';
import ActivityDetailsPriceSummaryMobileLoader from './components/loaders/ActivityDetailsPriceSummaryMobileLoader.vue';
import ActivityDetailsLeftPanelContent from './components/ActivityDetailsLeftPanelContent.vue';

const browserStore = useBrowserStore();
const activityDetailsPanelsStore = useActivityDetailsPanelsStore();
const activityDetailsModalsStore = useActivityDetailsModalsStore();
const activityDetailsStore = useActivityDetailsStore();
const activityDetailsMapStore = useActivityDetailsMapStore();
const mapStore = useMapStore();
const statsigEventsStore = useStatsigEventsStore();

interface ActivityDetailsProps {
  activityStaticData: ActivityStaticData | undefined;
  activityDynamicData: ActivityDynamicData | undefined;
}

const props = defineProps<ActivityDetailsProps>();

interface ActivityDetailsEmits {
  (event: 'close'): void;
  (event: 'select-tickets'): void;
}
const emit = defineEmits<ActivityDetailsEmits>();

function onSelectTickets(): void {
  statsigEventsStore.logActivityDetailsOpenTicketsSelector();
  browserStore.isSmallerThan900px
    ? activityDetailsPanelsStore.openTicketsSelectorPanel()
    : activityDetailsModalsStore.openTicketsSelectorModal();
}

onMounted(() => {
  document.body.style.overflow = 'hidden';
});

onUnmounted(() => {
  document.body.style.overflow = '';
});

watch(
  () => [props.activityStaticData, props.activityDynamicData],
  () => {
    if (
      props.activityStaticData &&
      !isEqual(
        props.activityStaticData,
        activityDetailsStore.activityStaticData
      )
    ) {
      activityDetailsStore.setActivityStaticData(props.activityStaticData);
    }

    if (props.activityDynamicData) {
      activityDetailsStore.setActivityDynamicData(props.activityDynamicData);
    }
  },
  { immediate: true }
);
</script>

<template>
  <ActivityDetailsPanels @select-tickets="emit('select-tickets')" />
  <ActivityDetailsModals @select-tickets="emit('select-tickets')" />

  <ActivityDetailsLayout
    class="activity-details"
    :is-loading="activityDetailsStore.isLoading"
    @close="emit('close')"
  >
    <template #header>
      <ActivityDetailsHeader
        :destination="activityDetailsStore.destinationCity"
        @close="emit('close')"
      />
    </template>
    <template #left-panel-gallery>
      <ActivityDetailsGalleryLoader v-if="activityDetailsStore.isLoading" />
      <ActivityDetailsGallery
        v-else-if="
          activityDetailsStore.galleryData.images &&
          activityDetailsStore.galleryData.productName &&
          activityDetailsStore.galleryData.productCode
        "
        :gallery-type="GalleryType.Masonry"
        :product-text="activityDetailsStore.galleryData.productName"
        :images="activityDetailsStore.galleryData.images"
        :activity-code="activityDetailsStore.galleryData.productCode"
        :amount-images-to-display="3"
        orientation="vertical"
        should-hide-button-text
        @close="emit('close')"
      />
    </template>
    <template #left-panel-content>
      <ActivityDetailsLeftPanelContent
        :is-loading="activityDetailsStore.isLoading"
        :main-data="activityDetailsStore.mainData"
        :description="activityDetailsStore.description"
        :distances="activityDetailsStore.distances"
        :map-configuration="{
          markers: activityDetailsMapStore.mapMarker,
          markerFormatters: activityDetailsMapStore.getMapMarkerFormatters(
            MapType.Static
          ),
          startRectangle: activityDetailsMapStore.initialMapView,
          mapType: MapTypeId.HYBRID,
        }"
        @open-dynamic-full-screen-map="
          activityDetailsMapStore.openDynamicMapModal
        "
      />
    </template>
    <template
      v-if="!browserStore.isSmallerThan900px"
      #right-panel-scrollable-header
    >
      <ActivityDetailsMainHeaderLoader v-if="activityDetailsStore.isLoading" />
      <ActivityDetailsMainHeader
        v-else-if="activityDetailsStore.mainData"
        :main-info="activityDetailsStore.mainData"
      />
    </template>
    <template
      v-if="!browserStore.isSmallerThan900px"
      #right-panel-scrollable-description
    >
      <ActivityDetailsMainDescription
        :main-description="activityDetailsStore.mainData.mainDescription"
      />
    </template>
    <template
      v-if="!browserStore.isSmallerThan900px"
      #right-panel-scrollable-description-read-more-button
    >
      <AppLinkButton
        :text="$t('planner.read_more')"
        size="m"
        @click="activityDetailsModalsStore.openFullDescriptionModal"
      />
    </template>
    <template v-if="!browserStore.isSmallerThan900px" #right-panel-sticky>
      <ActivityDetailsPriceSummaryDesktopLoader
        v-if="activityDetailsStore.isLoading"
      />
      <ActivityDetailsPriceSummaryDesktop
        v-else
        :from-cheapest-price="activityDetailsStore.activityTicketsLowestPrice"
        :selected-tickets="activityDetailsStore.selectedActivityTicketsInfo"
        @open-tickets-selector="onSelectTickets"
      />
    </template>
    <template v-if="browserStore.isSmallerThan900px" #footer>
      <ActivityDetailsPriceSummaryMobileLoader
        v-if="activityDetailsStore.isLoading"
      />
      <ActivityDetailsPriceSummaryMobile
        v-else
        :from-cheapest-price="activityDetailsStore.activityTicketsLowestPrice"
        :selected-tickets="activityDetailsStore.selectedActivityTicketsInfo"
        @open-tickets-selector="onSelectTickets"
      />
    </template>
  </ActivityDetailsLayout>

  <DynamicFullScreenMap
    v-if="activityDetailsMapStore.shouldShowFullscreenMap"
    :map-configuration="{
      markers: activityDetailsMapStore.mapMarker,
      markerFormatters: activityDetailsMapStore.getMapMarkerFormatters(
        MapType.Dynamic,
        mapStore.mapTypeId
      ),
      startRectangle: activityDetailsMapStore.initialMapView,
    }"
    @close="activityDetailsMapStore.closeDynamicMapModal"
  />
</template>

<style scoped lang="scss">
.activity-details {
  background-color: var(--color-white);
}
::-webkit-scrollbar-track {
  background: var(--color-white);
}
</style>
